/**
 * Ce qu'une cellule propose, et ce que la coche en retient.
 *
 * ⚠️ **Un fichier à part, et pas du rangement** : `react-refresh` interdit à un
 * module de composants d'exporter autre chose — la règle qui a déjà sorti
 * `components/ui/tokens.ts` de sa bibliothèque, et `setLabels.ts` de
 * `SetEntry`. Ces fonctions sont pures, elles n'ont rien à faire dans un module
 * qui rend du JSX.
 */

import type { ActualSetDTO, ProgressionDTO } from '../../../../shared/types';

/** Ce qui est saisi dans une ligne, tant que la coche n'a pas parlé. */
export interface SetValues {
  mesure: string;
  charge: string;
  /**
   * RPE — **remis le 29/08/2026, après usage**.
   *
   * 🔴 Il avait été retiré de la grille au passage à l'affichage Hevy, au motif
   * qu'il tenait la place de la charge dans une colonne trop étroite. Le retrait
   * traitait le bon symptôme au mauvais endroit : ce qui manquait de place,
   * c'était la **ligne**, pas le RPE. La ligne tient désormais sur deux rangs,
   * et il a retrouvé sa colonne.
   */
  rpe: string;
}

/**
 * Les seules valeurs de RPE proposées — **l'échelle de Hevy**, demandée après
 * usage le 05/09/2026.
 *
 * 🔴 **Une échelle discrète, et pas un champ libre.** Le RPE n'est pas une
 * mesure, c'est un **jugement** — « à combien de répétitions de l'échec
 * étais-je ? ». Entre 8 et 8,5 il y a une différence qu'on sait porter ; entre
 * 8,2 et 8,3 il n'y en a aucune, et un champ libre invite pourtant à la saisir.
 * Huit valeurs disent tout ce que l'échelle permet de dire honnêtement.
 *
 * ⚠️ **Rien sous 6, et aucun demi-point sous 7** : c'est l'échelle de Hevy
 * reprise telle quelle, et elle est bien construite. En dessous de 7 on est
 * loin de l'échec, donc la précision n'a plus d'objet — la finesse ne sert que
 * là où la décision se joue, entre 7 et 10.
 *
 * ⚠️ **La saisie libre disparaît, la donnée non.** Une série qui porte déjà un
 * RPE hors échelle — saisi avant ce changement, ou venu d'une cible de routine —
 * reste affichée telle quelle : c'est de l'historique, et le §4.1 vaut ici comme
 * partout. On ne réécrit rien, on cesse seulement d'en produire de nouveaux.
 */
export const RPE_VALUES: readonly number[] = [6, 7, 7.5, 8, 8.5, 9, 9.5, 10];

/** `8.5` → « 8,5 » — la virgule française, comme partout ailleurs. */
export function formatRpe(value: number): string {
  return value.toLocaleString('fr-FR');
}

/**
 * Bornes de la roue des répétitions *(05/09/2026)*.
 *
 * ⚠️ **1 et non 0** : une série faite à zéro répétition n'est pas une série
 * faite, c'est une série **passée** — et `status: 'skipped'` le dit déjà, sans
 * ambiguïté (§4.5). Offrir « 0 » inviterait à écrire un fait faux.
 *
 * ⚠️ **60 en haut**, ce qui couvre très largement la musculation, séries
 * longues au poids du corps comprises.
 */
export const REPS_MIN = 1;
export const REPS_MAX = 60;

const REPS_BASE: readonly number[] = Array.from(
  { length: REPS_MAX - REPS_MIN + 1 },
  (_, i) => REPS_MIN + i,
);

/**
 * Les crans de la roue, avec la valeur courante si elle sort des bornes.
 *
 * 🔴 **Même discipline que le RPE : on cesse d'en produire hors bornes, on n'en
 * réécrit aucune.** Une série portant 75 répétitions doit rester choisissable,
 * sinon l'ouvrir puis la refermer la ramènerait à 60 sans que personne ne l'ait
 * demandé — et c'est de l'historique (§4.1).
 *
 * ⚠️ **Vit ici et non dans `RepsWheel.tsx`** : `react-refresh` interdit à un
 * module de composants d'exporter autre chose. C'est la règle qui a déjà sorti
 * `components/ui/tokens.ts` de sa bibliothèque et `setLabels.ts` de `SetEntry`,
 * et elle a raison sur le fond — cette fonction est pure, elle n'a rien à faire
 * dans un module qui rend du JSX.
 */
export function repsValues(courant: number | null): readonly number[] {
  if (courant === null || !Number.isFinite(courant)) return REPS_BASE;
  if (courant >= REPS_MIN && courant <= REPS_MAX && Number.isInteger(courant)) return REPS_BASE;
  return [...REPS_BASE, courant].sort((a, b) => a - b);
}

export function nombre(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Ce qu'un filigrane vaut **une fois validé** — le haut d'une plage, sinon le
 * nombre lui-même.
 *
 * 🔴 **C'est le pendant indispensable de la plage en filigrane** *(06/09/2026,
 * demandé : « si le placeholder est une plage de valeur, mets la valeur la plus
 * haute »)*. Sans lui, `nombre('8-10')` rend `null` : cocher une série sans
 * rien saisir écrirait **aucune répétition** au lieu de dix. Le défaut serait
 * silencieux — la série passerait au vert, vide.
 *
 * ⚠️ **Le HAUT de la plage, et c'est ce qu'on veut dire par « j'ai fait ce
 * qu'il fallait ».** Le bas serait le minimum acceptable, la moyenne un nombre
 * que personne n'a fait. C'est aussi la valeur que la double progression
 * surveille (§4.4) : atteindre le haut de la plage est ce qui déclenche la
 * montée de charge.
 */
export function valeurDuFiligrane(texte: string): number | null {
  const trimmed = texte.trim();
  const plage = /^(\d+)\s*-\s*(\d+)$/.exec(trimmed);
  if (plage !== null) return Number(plage[2]);
  return nombre(trimmed);
}

/**
 * La valeur proposée pour une série — dans l'ordre : ce qu'on a fait la
 * dernière fois, puis la cible copiée du plan, puis rien.
 *
 * 🔴 **Elle est affichée en PLACEHOLDER, jamais écrite dans le champ.** Une
 * valeur pré-remplie en dur est indistinguable d'une valeur saisie : on
 * validerait 60 kg parce que c'était écrit, sans l'avoir décidé. En
 * placeholder, elle se voit, elle ne ment pas, et `readValues` la reprend si le
 * champ est resté vide — on obtient le pré-remplissage sans son défaut.
 *
 * ⚠️ C'est un écart au §4.5 (« les champs sont pré-remplis »), assumé : ce que
 * le §4.5 cherche, c'est qu'on **corrige plus qu'on ne saisisse**. Un
 * placeholder repris à la validation donne exactement cela, en une frappe de
 * moins et non une de plus.
 */
export function proposition(
  set: ActualSetDTO,
  precedente: ActualSetDTO | undefined,
  duree: boolean,
  /**
   * La charge proposée par la règle de progression (V2 étape 7, §4.4) — `null`
   * quand la routine n'en prescrit aucune.
   *
   * 🔴 **C'est un REMPLACEMENT du pré-remplissage de l'étape 6, pas un
   * mécanisme à côté.** Le même filigrane, décidé autrement : là où l'écran
   * reproposait la charge de la dernière fois, la règle propose désormais celle
   * de la dernière fois **plus l'incrément**, quand la plage a été tenue au RPE
   * cible. Rien d'autre ne change — ni le champ, ni la reprise par la coche.
   *
   * ⚠️ **Sans valeur par défaut, délibérément.** Un `= null` rendrait
   * l'oubli silencieux : un futur appelant retomberait sur le pré-remplissage
   * de l'étape 6 sans que rien ne le signale. Ici l'omission ne compile pas —
   * la règle est tenue par le type, pas par la vigilance.
   */
  progression: ProgressionDTO | null,
): SetValues {
  /*
   * 🔴 **La PLAGE prescrite passe devant le passage précédent** *(06/09/2026,
   * demandé : « mets la plage de reps en tant que placeholder de l'input »)*.
   *
   * Elle est ce qu'on doit faire **aujourd'hui** ; ce qu'on a fait la dernière
   * fois est déjà écrit dans la colonne « précédent », juste à gauche. Avant ce
   * changement, les répétitions du dernier passage s'affichaient **deux fois**
   * sur la même ligne — dans le rappel et dans le filigrane — et la plage, qui
   * n'était nulle part, avait dû être ajoutée en pastille à part le 29/08.
   *
   * ⚠️ **C'est un revirement, et il ferme la boucle.** Le 29/08 avait sorti la
   * plage du filigrane parce qu'elle y disparaissait : le champ n'affichait que
   * le **haut** (« 10 »), ce qui se lit « fais-en 10 ». En affichant la plage
   * entière (« 8-10 »), le filigrane redevient l'endroit juste — et la pastille
   * séparée n'a plus lieu d'être.
   *
   * ⚠️ Une cible **fixe** (min = max) reste un simple nombre : « 8-8 » ne dit
   * rien de plus que « 8 » et coûterait la place du rappel.
   */
  const plage =
    !duree &&
    set.targetRepsMin !== null &&
    set.targetRepsMax !== null &&
    set.targetRepsMin !== set.targetRepsMax
      ? `${String(set.targetRepsMin)}-${String(set.targetRepsMax)}`
      : null;

  const mesure = duree
    ? (precedente?.durationSeconds ?? set.targetDurationSeconds)
    : (set.targetRepsMax ?? precedente?.reps);
  /*
   * 🔴 **La progression ne vaut que pour les séries de TRAVAIL.** Elle est
   * calculée sur elles seules (cf. `services/progression.ts`), et l'appliquer à
   * un échauffement ou à une dégressive écrirait la charge de travail sur une
   * ligne qui n'en veut pas — une barre chargée à 62,5 kg proposée pour
   * s'échauffer. Les trois autres sortes gardent donc exactement le
   * comportement de l'étape 6.
   *
   * ⚠️ **Et le repli reste entier** : `weightKg` est `null` dès que la règle
   * n'a rien à proposer (poids du corps, durée, charges mélangées), et l'on
   * retombe alors sur la charge de la même série au dernier passage, puis sur
   * la cible copiée. Aucun cas ne perd son filigrane.
   */
  const proposee = set.setKind === 'work' ? progression?.weightKg : null;
  const charge = proposee ?? precedente?.weightKg ?? set.targetWeightKg;
  /*
   * ⚠️ **Le RPE proposé est la CIBLE, jamais celui du dernier passage.** Les
   * trois autres valeurs rappellent ce qu'on a fait ; le RPE, lui, est ce qu'on
   * *vise* — proposer celui de la dernière fois reviendrait à suggérer de
   * refaire le même effort, ce qui est précisément ce que la progression
   * décidera (étape 7), pas cet écran.
   */
  const rpe = set.targetRpe;
  return {
    mesure: plage ?? (mesure === null || mesure === undefined ? '' : String(mesure)),
    charge: charge === null || charge === undefined ? '' : String(charge),
    rpe: rpe === null || rpe === undefined ? '' : String(rpe),
  };
}

/**
 * Ce que la coche écrit — la saisie si elle existe, la proposition sinon.
 *
 * ⚠️ **La nature de la mesure se lit dans la série** :
 * `targetDurationSeconds !== null` **est** « série en durée ». C'est la dualité
 * de `routine_line` (migration `0010`) portée jusqu'à la cellule, et c'est
 * précisément pour qu'elle tienne ici qu'`actual_set` a dû la reprendre.
 */
export function readValues(
  set: ActualSetDTO,
  precedente: ActualSetDTO | undefined,
  saisi: SetValues | undefined,
  /**
   * ⚠️ **La MÊME proposition que celle affichée en filigrane**, et c'est tout
   * l'intérêt de la faire passer ici aussi : la coche reprend exactement ce
   * qu'on lisait dans le champ. Deux sources ne pourraient que diverger — on
   * validerait une charge qui n'a jamais été montrée.
   */
  progression: ProgressionDTO | null,
): {
  reps: number | null;
  durationSeconds: number | null;
  weightKg: number | null;
  rpe: number | null;
} {
  const duree = set.targetDurationSeconds !== null;
  const propose = proposition(set, precedente, duree, progression);
  /*
   * ⚠️ **`valeurDuFiligrane` et non `nombre`** : le filigrane des répétitions
   * peut être une **plage** (« 8-10 »), que `nombre()` ne sait pas lire — elle
   * rendrait `null`, et cocher sans rien saisir écrirait une série vide.
   */
  const mesure = nombre(saisi?.mesure ?? '') ?? valeurDuFiligrane(propose.mesure);
  const charge = nombre(saisi?.charge ?? '') ?? nombre(propose.charge);
  const rpe = nombre(saisi?.rpe ?? '') ?? nombre(propose.rpe);

  return {
    reps: duree ? null : mesure,
    durationSeconds: duree ? mesure : null,
    weightKg: charge,
    rpe,
  };
}
